import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import BuyButton, { BuyButtonProps } from '@wix/da-ds/pkg/Buttons/BuyButton';
import {
  BiData,
  BiContextProvidedValue,
  EditSaleBiEvent,
  BiEvent,
} from '@wix/da-bi/pkg/events';
import { useSellActionLogic } from '../hooks/useSellActionLogic';

import s from './SellButton.scss';

interface Props {
  deviation: PapiDeviation;
  biData?: BiEvent;
}

const SellButton: FC<Props> = ({ deviation, biData }) => {
  const { t } = useTranslation();
  const sellState = useSellActionLogic(deviation);

  const baseButtonProps: BuyButtonProps = {
    className: s['root'],
    size: 'medium' as const,
    variant: 'outline' as const,
    rounded: true,
    biData,
  };

  // Handle unsellable types
  if (sellState.isUnsellable) {
    return (
      <BuyButton {...baseButtonProps} disabled>
        {t('studio.table.actions.sell.unsellable')}
      </BuyButton>
    );
  }

  // Handle sold deviations
  if (sellState.isSold) {
    return (
      <BuyButton {...baseButtonProps} disabled>
        {t('studio.table.actions.sell.sold')}
      </BuyButton>
    );
  }

  // Handle resale state
  if (sellState.isOnResale) {
    return (
      <BuyButton {...baseButtonProps} disabled>
        {t('studio.table.actions.sell.resale')}
      </BuyButton>
    );
  }

  // Handle edit cases (on sale, pending, or PCP)
  if (sellState.isOnSale || sellState.isPending || sellState.isPcp) {
    return (
      <BuyButton
        {...baseButtonProps}
        onClick={
          sellState.isPcp
            ? sellState.callbacks.onEditPcpSaleClick
            : sellState.callbacks.onEditAdoptableSaleClick
        }
        biData={BiData<EditSaleBiEvent>({
          evid: 354,
          deviationid: deviation.deviationId,
          sectionname: BiContextProvidedValue,
        })}
      >
        {t('studio.table.actions.sell.edit')}
      </BuyButton>
    );
  }

  // Default sell action
  return (
    <BuyButton {...baseButtonProps} onClick={sellState.callbacks.onSellClick}>
      {t('studio.table.actions.sell')}
    </BuyButton>
  );
};
SellButton.displayName = 'SellButton';

export default SellButton;
